import * as React from "react";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";

/**
 * A rule's period — "10s", "1m", "1h", "1d".
 *
 * It is stored as that string and always has been, so this composes back to exactly the
 * same value and nothing downstream knows the control changed. What changed is the asking:
 * it was a free-text box whose format lived in a placeholder and in a regex that only spoke
 * on submit, so the first anyone heard about `10s` was after getting it wrong.
 *
 * `PERIOD_PATTERN` stays the field's validator either way — it guards values stored before
 * this control existed, which this cannot.
 */
const PERIOD_UNITS: KuikaSelectboxOption[] = [
  { value: "s", label: "seconds" },
  { value: "m", label: "minutes" },
  { value: "h", label: "hours" },
  { value: "d", label: "days" }
];

const DEFAULT_UNIT = "m";

export interface IRulePeriodInputProps {
  /** Both supplied by the enclosing `Form.Item`. */
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
}

export interface IRulePeriodInputState {
  /**
   * The unit lives here as well as in the composed string, because clearing the amount has
   * to empty the field — and an empty field carries no unit to read back. Without somewhere
   * to remember it, deleting the amount of a rule set to "10 seconds" silently moved it to
   * minutes, changing a value the user had not touched.
   */
  unit: string;
}

interface IParsedPeriod {
  amount: string;
  unit: string;
}

/**
 * Deliberately as strict as `PERIOD_PATTERN`: a value this cannot read shows an empty amount
 * and fails the validator, which is the honest outcome. Accepting a bare `"10"` here would
 * be worse than rejecting it — the row would render a confident "10 | minutes" while the
 * stored value stayed `"10"` and failed on submit, with nothing on screen looking wrong.
 */
const parsePeriod = (value: string | undefined): IParsedPeriod => {
  const raw: string = String(value ?? "").trim();
  const match: RegExpMatchArray | null = raw.match(/^(\d+)\s*([smhd])$/i);
  if (!match) return { amount: "", unit: DEFAULT_UNIT };
  return { amount: match[1], unit: match[2].toLowerCase() };
};

export class RulePeriodInput extends React.PureComponent<IRulePeriodInputProps, IRulePeriodInputState> {
  constructor(props: IRulePeriodInputProps) {
    super(props);
    this.state = { unit: parsePeriod(props.value).unit };
  }

  // A value arriving from outside — another rule loaded into this row — wins, but only when
  // it actually carries a unit. An empty one leaves the remembered unit where it is.
  static getDerivedStateFromProps(
    props: IRulePeriodInputProps,
    state: IRulePeriodInputState
  ): IRulePeriodInputState | null {
    const parsed: IParsedPeriod = parsePeriod(props.value);
    if (parsed.amount === "" || parsed.unit === state.unit) return null;
    return { unit: parsed.unit };
  }

  // An empty amount emits an empty string rather than a bare unit, so `required` still has
  // something to fail on.
  emit = (amount: string, unit: string): void => {
    this.props.onChange?.(amount === "" ? "" : `${amount}${unit}`);
  };

  handleAmountChange = (_event: React.ChangeEvent<HTMLInputElement>, next: string | number): void => {
    const digits: string = String(next ?? "").replace(/\D/g, "");
    this.emit(digits, this.state.unit);
  };

  handleUnitChange = (unit: string | number): void => {
    const next: string = String(unit);
    this.setState({ unit: next });
    this.emit(parsePeriod(this.props.value).amount, next);
  };

  render(): React.ReactNode {
    const parsed: IParsedPeriod = parsePeriod(this.props.value);

    return (
      <span className="kuika-rate-limit-rules__period">
        <KuikaInput
          size="md"
          inputMode="numeric"
          placeholder="10"
          disabled={this.props.disabled}
          value={parsed.amount}
          onChange={this.handleAmountChange}
        />
        <KuikaSelectbox
          size="md"
          disabled={this.props.disabled}
          value={this.state.unit}
          onChange={this.handleUnitChange}
          options={PERIOD_UNITS}
        />
      </span>
    );
  }
}
